import React from "react";
import { ASSETS } from "@/lib/assets";
import { ChevronLeft, Swords, Loader } from "lucide-react";
import { Stat } from "./shared";

export const CommandSetup = ({
  navigate, opts, state,
  engagement, setEngagement, commitment, setCommitment,
  surround, setSurround, pveTactic, setPveTactic,
  assignedOfficerId, setAssignedOfficerId, aliveOfficers,
  briefing, loadingBrief, fetchBrief,
  resolveCommand, resolving,
  committedCount, projectedHumanMorale, surroundEligible,
}) => (
  <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-10 lg:py-14">
    <button onClick={() => navigate("/command")} className="btn-ghost mb-6 flex items-center gap-1" data-testid="battle-back-button">
      <ChevronLeft className="w-3 h-3" /> COMMAND
    </button>

    <p className="tac-label fade-up">BATTLE PLANNING // {opts.preview.region_name}</p>
    <h1 className="font-serif-h text-5xl lg:text-7xl uppercase text-bone leading-none mt-3 fade-up"
        data-testid="battle-name-heading">
      {opts.preview.battle_name}
    </h1>

    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-12">
      <div className="lg:col-span-7 narrative-panel relative fade-up delay-1">
        <div className="absolute inset-0 opacity-15 pointer-events-none">
          <img src={ASSETS.horde} alt="" className="w-full h-full object-cover mix-blend-overlay" />
        </div>
        <div className="relative">
          <p className="tac-label mb-3">INTEL</p>
          <p className="text-base leading-relaxed text-[var(--text-main)] italic" data-testid="battle-intel">
            "{opts.preview.intel}"
          </p>

          <div className="grid grid-cols-4 gap-2 mt-8">
            <Stat label="HUMANS" value={opts.preview.humans} />
            <Stat label="WEAPONS" value={`${Math.round(opts.preview.weapons * 100)}%`} />
            <Stat label="FORTIFIED" value={`${Math.round(opts.preview.fortification * 100)}%`} />
            <Stat label="THEIR MORALE" value={`${projectedHumanMorale}/100`}
                  good={projectedHumanMorale < 50} />
          </div>

          {opts.preview.min_horde && committedCount < opts.preview.min_horde && (
            <div className="mt-4 border-l-2 border-[var(--danger)] pl-3" data-testid="min-horde-warning">
              <p className="tac-label text-[var(--danger)]">FORCE TOO SMALL</p>
              <p className="text-xs text-[var(--text-main)] mt-1 leading-relaxed">
                You need at least <span className="text-bone">{opts.preview.min_horde}</span> committed zombies for this assault. You have <span className="text-bone">{committedCount}</span>.
                Increase commitment, grow your horde, or pick a smaller target.
              </p>
            </div>
          )}

          {briefing && (
            <div className="mt-8 pt-8 border-t border-subtle">
              <p className="tac-label mb-3">COMMANDER'S BRIEFING</p>
              <p className="text-sm leading-relaxed text-[var(--text-main)] whitespace-pre-line" data-testid="battle-briefing-text">
                {briefing}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="lg:col-span-5 flex flex-col gap-5 fade-up delay-2">
        <div className="tactical-card">
          <p className="tac-label">YOUR HORDE</p>
          <p className="font-serif-h text-4xl text-bone mt-1" data-testid="battle-horde-size">{state.personal_horde}</p>
          <p className="text-xs text-muted-h mt-1">UNDER DIRECT COMMAND</p>
        </div>

        <div className="tactical-card">
          <div className="flex items-baseline justify-between mb-3">
            <span className="tac-label">ENGAGEMENT</span>
            <span className="font-mono text-bone text-sm" data-testid="engagement-value">{engagement}/100</span>
          </div>
          <input type="range" min="0" max="100" value={engagement}
                 onChange={(e) => setEngagement(Number(e.target.value))}
                 className="blood-slider" data-testid="engagement-slider" />
          <p className="text-[0.7rem] text-muted-h mt-2">
            {engagement < 25 ? "STAY BACK" : engagement < 60 ? "CO-ORDINATE" : engagement < 85 ? "FRONT LINE" : "LEAD THE CHARGE"}
          </p>
        </div>

        <div className="tactical-card">
          <div className="flex items-baseline justify-between mb-3">
            <span className="tac-label">HORDE COMMITTED</span>
            <span className="font-mono text-bone text-sm" data-testid="commitment-value">
              {commitment}% — {committedCount}
            </span>
          </div>
          <input type="range" min="10" max="100" value={commitment}
                 onChange={(e) => setCommitment(Number(e.target.value))}
                 className="blood-slider" data-testid="commitment-slider" />
        </div>

        {/* Tactic picker (PvE RPS) */}
        <div data-testid="tactic-picker" className="mt-2">
          <p className="tac-label mb-2">TACTIC</p>
          <div className="grid grid-cols-3 gap-2">
            {[
              { key: "overwhelm", label: "OVERWHELM", blurb: "Mass. +20% power, no fortification bypass." },
              { key: "ambush",    label: "AMBUSH",    blurb: "Flank. +5% power, -25% fortification, morale intact." },
              { key: "siege",     label: "SIEGE",     blurb: "Drain. -10% power, -40% fortification, -15% morale." },
            ].map((t) => {
              const active = pveTactic === t.key;
              return (
                <button key={t.key}
                  onClick={() => setPveTactic(t.key)}
                  className={`text-left p-2 border transition-all ${
                    active ? "border-blood bg-[#0F0707]"
                           : "border-[var(--border-subtle)] hover:border-blood/60"
                  }`}
                  data-testid={`pve-tactic-${t.key}`}
                >
                  <p className="font-mono text-[0.6rem] tracking-[0.2em] text-blood">{t.label}</p>
                  <p className="text-[0.7rem] text-bone/80 mt-1 leading-snug">{t.blurb}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Officer commit picker */}
        {aliveOfficers.length > 0 && (
          <div data-testid="officer-picker" className="mt-2">
            <div className="flex items-baseline justify-between mb-2">
              <span className="tac-label">COMMIT AN OFFICER</span>
              <span className="font-mono text-[0.6rem] text-muted-h tracking-widest">
                MATCHING TACTIC = +10% CONVERSION · RISK: THEY DIE ON DEFEAT
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setAssignedOfficerId(null)}
                className={`px-3 py-1.5 border text-[0.7rem] font-mono tracking-widest transition-all ${
                  !assignedOfficerId ? "border-blood text-blood bg-[#0F0707]"
                                     : "border-[var(--border-subtle)] text-muted-h hover:border-blood/60"
                }`}
                data-testid="officer-pick-none"
              >
                NONE
              </button>
              {aliveOfficers.map((o) => {
                const match = o.specialty === pveTactic;
                const active = assignedOfficerId === o.id;
                return (
                  <button
                    key={o.id}
                    onClick={() => setAssignedOfficerId(o.id)}
                    className={`px-3 py-1.5 border text-left transition-all ${
                      active ? "border-blood bg-[#0F0707]"
                             : "border-[var(--border-subtle)] hover:border-blood/60"
                    }`}
                    data-testid={`officer-pick-${o.id}`}
                  >
                    <span className="font-serif-h text-sm text-bone">{o.name}</span>
                    <span className={`ml-2 font-mono text-[0.55rem] tracking-widest ${
                      match ? "text-[#7FBA9D]" : "text-muted-h"
                    }`}>
                      {(o.specialty || "").toUpperCase()}{match ? " · SYNERGY" : ""}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <button
          onClick={() => surroundEligible && setSurround(!surround)}
          disabled={!surroundEligible}
          className={`tactical-card text-left transition-all ${
            surround ? "border-blood bg-[#0F0707]" : ""
          } ${!surroundEligible ? "opacity-40 cursor-not-allowed" : "hover:border-blood"}`}
          data-testid="surround-toggle"
        >
          <div className="flex items-center justify-between">
            <span className="tac-label">SURROUND BATTLEFIELD</span>
            <span className={`font-mono text-xs ${surround ? "text-blood" : "text-muted-h"}`}>
              {surround ? "ACTIVE" : "OFF"}
            </span>
          </div>
          <p className="text-[0.7rem] text-muted-h mt-2 leading-relaxed">
            {surroundEligible
              ? "Encircle them. 9/10: panic & desertion (mass kills). 1/10: they sally out — you take losses."
              : "Requires 60%+ commitment to encircle."}
          </p>
        </button>

        <button onClick={fetchBrief} disabled={loadingBrief} className="btn-ghost" data-testid="briefing-button">
          {loadingBrief ? <><Loader className="w-3 h-3 animate-spin" /> ...</> : "REQUEST BRIEFING"}
        </button>
        <button onClick={resolveCommand}
                disabled={resolving || (opts.preview.min_horde && committedCount < opts.preview.min_horde)}
                className="btn-blood" data-testid="engage-button">
          {resolving ? "RESOLVING..." : <><Swords className="w-3 h-3" /> ENGAGE NOW</>}
        </button>
      </div>
    </div>
  </div>
);
